// SEOENG-56: the site's acquisition CTAs push their own, named dataLayer events so GA4 can
// measure view-to-registration per page. Two events, one contract:
//
//   content_register_click  a visitor acted on an acquisition CTA (download modal opened,
//                           mobile store redirect, or navigation to the app-download page)
//   app_store_click         a visitor followed a store / Adjust link (badge, tracked link)
//
// Both carry `product` (gti | gtg), `page_path`, `page_type`, `cta_location`, `cta_destination`
// and `cta_link_host`. GTM consumes them with Custom Event triggers (never Click triggers: the
// deferred loader in gatsby-ssr.js only attaches click listeners once gtm.js has run, so a
// first-interaction CTA tap is invisible to a Click trigger, while a dataLayer push queues and is
// replayed when the container initialises). Pure builders live here so the contract is unit-tested;
// the push itself is a thin, never-throwing wrapper.
import { isGtiPathname } from './campaign-attribution.js';

export const REGISTER_CTA_EVENT = 'content_register_click';
export const APP_STORE_CLICK_EVENT = 'app_store_click';

export const CTA_DESTINATION = Object.freeze({
  DOWNLOAD_MODAL: 'download_modal',
  STORE_REDIRECT: 'store_redirect',
  APP_DOWNLOAD_PAGE: 'app_download_page'
});

export const STORE = Object.freeze({
  APP_STORE: 'app_store',
  PLAY_STORE: 'play_store',
  ADJUST: 'adjust',
  OTHER: 'other'
});

const UNLABELED = 'unlabeled';

function canUseBrowser() {
  return typeof window !== 'undefined';
}

function currentPathname() {
  return canUseBrowser() && window.location ? window.location.pathname || '' : '';
}

export function productForPathname(pathname = '') {
  return isGtiPathname(pathname) ? 'gti' : 'gtg';
}

// Coarse page family for GA4 breakdowns; the exact page rides page_path / page_location. Kept
// deliberately small: only families the content team reports on today.
export function classifyPageType(pathname = '') {
  const path = (pathname || '').toLowerCase();
  const trimmed = path.replace(/\/+$/, '');

  if (trimmed === '' || trimmed === '/id' || trimmed === '/en') return 'home';
  if (/\/app-download(\/|$)/.test(path)) return 'app_download';
  if (/^\/sem(\/|$)/.test(path)) return 'sem';
  // Article hubs: the legacy buckets (blog, insider, panduan-investasi-saham-as) and the Option B
  // taxonomy hubs (/id/edukasi/, and /en/learn/ once the EN hub is built; src/data/optionb-map.js).
  if (/\/(blog|blogs|edukasi|learn|insider|panduan-investasi-saham-as)(\/|$)/.test(path)) {
    return 'blog';
  }
  if (/\/(news|berita)(\/|$)/.test(path)) return 'news';
  // Its own family: a separate CDN bucket, hub and sitemap prefix, and daily commentary rather than
  // evergreen education, so the content team can read its registration rate apart from blog.
  if (/\/market-outlook(\/|$)/.test(path)) return 'market_outlook';
  if (/\/whats-new(\/|$)/.test(path)) return 'whats_new';
  if (/\/(saham-as|us-stock|cara-beli-saham-)/.test(path)) return 'stock';
  if (/\/(tradingview|ideas)(\/|$)/.test(path)) return 'ideas';
  return 'other';
}

export function linkHost(link = '') {
  if (!link || typeof link !== 'string') return '';
  try {
    return new URL(link, 'https://www.heygotrade.com').hostname;
  } catch (error) {
    return '';
  }
}

export function classifyStore(link = '') {
  const host = linkHost(link);
  if (!host) return STORE.OTHER;
  if (host === 'apps.apple.com' || host.endsWith('.apps.apple.com')) return STORE.APP_STORE;
  if (host === 'play.google.com') return STORE.PLAY_STORE;
  if (host.endsWith('.go.link') || host.endsWith('.adj.st') || host === 'go.heygotrade.com') {
    return STORE.ADJUST;
  }
  return STORE.OTHER;
}

function baseFields({ pathname, product, ctaLocation, link }) {
  const path = pathname ?? currentPathname();
  return {
    product: product || productForPathname(path),
    page_path: path,
    page_type: classifyPageType(path),
    cta_location: ctaLocation || UNLABELED,
    cta_link_host: linkHost(link)
  };
}

export function buildRegisterCtaEvent({
  pathname,
  product = null,
  ctaLocation = null,
  destination = CTA_DESTINATION.DOWNLOAD_MODAL,
  link = ''
} = {}) {
  return {
    event: REGISTER_CTA_EVENT,
    cta_destination: destination,
    ...baseFields({ pathname, product, ctaLocation, link })
  };
}

export function buildAppStoreClickEvent({
  pathname,
  product = null,
  ctaLocation = null,
  link = ''
} = {}) {
  return {
    event: APP_STORE_CLICK_EVENT,
    store: classifyStore(link),
    ...baseFields({ pathname, product, ctaLocation, link })
  };
}

// Pushes onto window.dataLayer, creating it when the loader has not run yet (the deferred GTM
// snippet creates it inline in <head>, so this is belt and braces). Returns whether a push
// happened; never throws, because tracking must not break a CTA.
export function pushDataLayerEvent(event, win = canUseBrowser() ? window : null) {
  if (!win || !event || typeof event !== 'object') return false;
  try {
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push(event);
    return true;
  } catch (error) {
    return false;
  }
}

export function trackRegisterCtaClick(options = {}) {
  return pushDataLayerEvent(buildRegisterCtaEvent(options));
}

export function trackAppStoreClick(options = {}) {
  return pushDataLayerEvent(buildAppStoreClickEvent(options));
}
